import { useTranslation } from '../../../../lib/i18n';
import { PageLoader, Typography } from '../../ui';
import { AgentsEmptyIntro } from '../AgentsEmptyIntro';
import { useYourAgents } from '../use-your-agents';
import { MobileDestinationList } from './MobileDestinationList';
import { MobileTeamPanel } from './MobileTeamPanel';
import { TasksSummary } from '../../tasks/TasksSummary';
import { GoalsSummary } from '../../goals/GoalsSummary';
import { RoutinesSummary } from '../../routines/RoutinesSummary';
import { FilesSummaryCard } from '../FilesSummaryCard';
import { ContactsSummaryCard } from '../ContactsSummaryCard';
import { AppsSummaryCard } from '../AppsSummaryCard';
import { useSummaryTilesReady } from '../use-summary-tiles-ready';
import styles from './YourAgentsMobile.module.css';

// Your Agents, narrow (Figma `8509:36899`). Three stacked regions:
//
//   1. the title with the workspace task strip tucked straight under it,
//   2. a full-bleed team band — the headline, the two team actions, the genome, and the
//      roster as a horizontal card carousel,
//   3. the management destinations as full-width rows.
//
// The screen owns its own title (32px, tight to the strip) rather than taking AppShell's
// 40px page header, so `pages/agents.tsx` hands it an empty title on narrow.
export function YourAgentsMobile() {
  const { t } = useTranslation();
  const { team, loading, agents, skills, maestro, selectedId, setSelected, openSkills, addAgent } = useYourAgents();
  // The same gate the wide screen uses — the stack pops in tile by tile without it.
  const tilesReady = useSummaryTilesReady();

  if (team.status === 'error') {
    return (
      <Typography variant="p1" color="secondary" className={styles.error}>
        {t('agents.error')}
      </Typography>
    );
  }

  // Nothing until both reads land: see `loading` in useYourAgents for why the whole screen
  // waits rather than filling in around a half-known team.
  if (loading) return <PageLoader />;

  // No team yet → the intro replaces the whole screen, matching the wide screen and native.
  if (agents.length === 0) return <AgentsEmptyIntro />;

  return (
    <div className={styles.body}>
      <header className={styles.head}>
        <Typography variant="h1" color="primary" component="h1">
          {t('agents.titleShort')}
        </Typography>
      </header>

      {/* The three entry points — Tasks · Routines · Files — as compact single-row tiles with 12px
          icon-count badges (Figma 15311:197080), grouped above the team band in native's order. Each
          shows a count per non-empty status and drills into its full list; Files also carries the
          "N files · M spaces" line and an outlined "N new" pill. Desktop keeps the wider summaries. */}
      {tilesReady && (
        <div className={styles.entryCards}>
          <TasksSummary entryRow emptyLabelKey="agents.tasksOngoing" />
          <GoalsSummary entryRow />
          <RoutinesSummary entryRow />
          <FilesSummaryCard entryRow />
          <AppsSummaryCard entryRow />
          <ContactsSummaryCard entryRow title={t('home.map.outreach')} />
        </div>
      )}

      <MobileTeamPanel
        agents={agents}
        skillNames={skills.map((skill) => skill.name)}
        skillCount={skills.length}
        maestro={maestro}
        selectedId={selectedId}
        onSelect={setSelected}
        onOpenSkills={openSkills}
        onAddAgent={addAgent}
      />

      <MobileDestinationList />
    </div>
  );
}
